import { motion } from "framer-motion";
import { ArrowRight, MapPin, Star, Facebook } from "lucide-react";
import { IMAGES, CONTACT } from "@/data/site";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Completed garden patio transformation by CWood Construction"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-slate-900/30" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2 text-[#7fb0ff] mb-5"
          >
            <MapPin size={16} />
            <span className="text-sm uppercase tracking-[0.2em] font-semibold">
              Huddersfield, West Yorkshire
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.1]"
          >
            Building Quality.
            <br />
            <span className="text-[#7fb0ff]">Creating Outdoor Spaces.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl"
          >
            Professional construction and landscaping services across Huddersfield
            and West Yorkshire. From patios and brickwork to complete garden
            transformations.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollTo("contact")}
              className="group bg-[#1656b8] text-white rounded-lg px-8 py-4 font-semibold text-lg flex items-center justify-center gap-2 hover:bg-[#0f3d83] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl"
            >
              Get a Free Quote
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-work-button"
              onClick={() => scrollTo("projects")}
              className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/40 rounded-lg px-8 py-4 font-semibold text-lg hover:bg-white hover:text-[#1656b8] transition-all duration-300"
            >
              View Our Work
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-10 flex items-center gap-3"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className="fill-[#F59E0B] text-[#F59E0B]" />
              ))}
            </div>
            <span className="text-slate-200 text-sm font-medium">
              Trusted by homeowners across West Yorkshire
            </span>
          </motion.div>

          <motion.a
            href={CONTACT.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-facebook-link"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            whileHover={{ y: -3 }}
            className="group mt-8 inline-flex items-center gap-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 pl-2 pr-5 py-2 hover:bg-white/15 hover:border-[#1877F2]/60 transition-colors duration-300"
          >
            <span className="relative flex h-10 w-10 items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-[#1877F2] opacity-60 animate-ping" />
              <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#1877F2] shadow-lg shadow-[#1877F2]/40 transition-transform duration-300 group-hover:scale-110">
                <Facebook size={20} className="fill-white text-white" />
              </span>
            </span>
            <span className="flex flex-col leading-tight text-left">
              <span className="text-white font-semibold text-sm">Follow us on Facebook</span>
              <span className="text-slate-300 text-xs">See our latest projects &amp; reviews</span>
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
};
